import React, {
  FC,
  useMemo,
  useState,
  useEffect,
  useRef,
  useLayoutEffect,
} from "react";
import { FaSpinner, FaExclamationTriangle, FaTimes } from "react-icons/fa";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { MatchListEntry } from "../types";
import useMatchEndpoint, { MatchCounters } from "../hooks/useMatchEndpoint";
import MatchesContainer from "./MatchesContainer";
import BottomNavbar from "../components/BottomNavbar";
import { Grid, List } from "lucide-react";
import { toast } from "react-toastify";
import {
  handleManageProfile,
  handleShortList,
  handleSwitchProfile,
  handleViewProfile,
} from "../helpers/matches.helper";
import { postRequest, putRequest } from "../helpers/api.helper";
import Gnavbar from "./Gnavbar";
import ShareModal from "./ShareModal";
import { sendLoginCredentialsOnWhatsApp } from "../helpers/whatsapp.helper";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { hasActiveMembership } from "../utils/bureau";
import { useLanguage } from "../context/LanguageContext";
import { MatchCardAction } from "../types";

type FilterOption = {
  id: string;
  label: string;
  count: number;
  predicate: (match: MatchListEntry) => boolean;
};

type CounterFiltersProps = {
  options: Array<Pick<FilterOption, "id" | "label" | "count">>;
  activeFilterId: string | null;
  onFilterChange: (id: string | null) => void;
  totalCount: number;
  className?: string;
  status?: "other" | "own";
};

const formatCounterLabel = (value: string): string => {
  if (value.toLowerCase() === "inactive") {
    return "New";
  }
  const cleaned = value.replace(/[._-]+/g, " ").trim();
  if (!cleaned.length) {
    return value;
  }
  return cleaned
    .split(" ")
    .map((segment) =>
      segment.length
        ? segment.charAt(0).toUpperCase() + segment.slice(1)
        : segment,
    )
    .join(" ");
};

const normalizeKeyValue = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  return String(value)
    .toLowerCase()
    .replace(/[\s_-]+/g, "")
    .trim();
};

const candidateNestedKeys = [
  "profile",
  "user",
  "targetUser",
  "member",
  "profileDetails",
  "details",
];

const getCandidateSources = (
  match: MatchListEntry,
): Record<string, unknown>[] => {
  const sources: Record<string, unknown>[] = [];
  const rawProfile = match;
  if (rawProfile) {
    sources.push(rawProfile);
  }
  return sources;
};

const getValuesForKeys = (match: MatchListEntry, keys: string[]): unknown[] => {
  const values: unknown[] = [];
  const sources = getCandidateSources(match);

  sources.forEach((source) => {
    keys.forEach((key) => {
      if (key in source) {
        values.push((source as Record<string, unknown>)[key]);
      }
    });
  });

  return values;
};

const matchHasNormalizedValue = (
  match: MatchListEntry,
  keys: string[],
  expected: string,
): boolean => {
  const normalizedExpected = normalizeKeyValue(expected);
  if (!normalizedExpected) return false;

  const values = getValuesForKeys(match, keys);

  for (const value of values) {
    if (Array.isArray(value)) {
      if (
        value.some((item) => normalizeKeyValue(item) === normalizedExpected)
      ) {
        return true;
      }
    } else {
      const normalizedValue = normalizeKeyValue(value);
      if (normalizedValue && normalizedValue === normalizedExpected) {
        return true;
      }
    }
  }
  return false;
};

const createPredicate = (
  filterKey: string,
  parentKey?: string,
): ((match: MatchListEntry) => boolean) => {
  const normalizedKey = normalizeKeyValue(filterKey);

  if (
    filterKey.toLowerCase().startsWith("createdby") &&
    filterKey.length > "createdby".length
  ) {
    const creatorValue = filterKey.slice("createdBy".length);
    const creatorKeys = [
      "createdBy",
      "created_by",
      "creator",
      "creatorType",
      "createdByType",
      "created_by_type",
      "createdByRole",
      "created_by_role",
      "createdBySource",
      "created_by_source",
    ];
    return (match) => matchHasNormalizedValue(match, creatorKeys, creatorValue);
  }

  if (parentKey === "servicePreferences") {
    const candidateKeys = [
      "servicePreference",
      "servicePreferences",
      "serviceType",
      "service_type",
      "filterTags",
      "tags",
    ];
    return (match) => matchHasNormalizedValue(match, candidateKeys, filterKey);
  }

  if (filterKey === "active" || filterKey === "inactive") {
    const paymentKeys = [
      "paymentStatus",
      "payment_status",
      "planType",
      "plan_type",
      "accountType",
      "account_type",
      "subscriptionType",
      "subscription_type",
      "filterTags",
      "tags",
    ];
    return (match) => {
      if (filterKey === "inactive") {
        return matchHasNormalizedValue(match, paymentKeys, "unassigned");
      }
      return ["free", "paid", "redzone"].some((status) =>
        matchHasNormalizedValue(match, paymentKeys, status),
      );
    };
  }

  if (filterKey === "paid" || filterKey === "free" || filterKey === "redzone") {
    const paymentKeys = [
      "paymentStatus",
      "payment_status",
      "planType",
      "plan_type",
      "accountType",
      "account_type",
      "subscriptionType",
      "subscription_type",
      "filterTags",
      "tags",
    ];
    return (match) => matchHasNormalizedValue(match, paymentKeys, filterKey);
  }

  if (
    filterKey === "callback" ||
    filterKey === "notresponding" ||
    filterKey === "not_responding"
  ) {
    const followUpKeys = [
      "followUpStatus",
      "followupStatus",
      "follow_up_status",
      "responseStatus",
      "response_status",
      "tags",
      "filterTags",
    ];
    return (match) => matchHasNormalizedValue(match, followUpKeys, filterKey);
  }

  const candidateKeys = [
    filterKey,
    `${filterKey}Status`,
    `${filterKey}_status`,
    `${filterKey}Type`,
    `${filterKey}_type`,
    "filterTags",
    "tags",
  ];

  if (normalizedKey === "true" || normalizedKey === "false") {
    return (match) => matchHasNormalizedValue(match, candidateKeys, filterKey);
  }

  return (match) => matchHasNormalizedValue(match, candidateKeys, filterKey);
};

const buildFilterOptions = (
  counters: MatchCounters | null | undefined,
): FilterOption[] => {
  if (!counters) {
    return [];
  }

  const options: FilterOption[] = [];
  const processedKeys = new Set<string>();

  const addOption = (
    id: string,
    filterKey: string,
    label: string,
    count: number,
    parentKey?: string,
  ) => {
    options.push({
      id,
      label,
      count,
      predicate: createPredicate(filterKey, parentKey),
    });
  };

  const handleEntry = (
    key: string,
    value: number | Record<string, unknown> | undefined,
  ) => {
    if (typeof value === "number") {
      addOption(key, key, formatCounterLabel(key), value);
      processedKeys.add(key);
    } else if (value && typeof value === "object") {
      Object.entries(value).forEach(([childKey, childValue]) => {
        if (typeof childValue === "number") {
          const optionId = `${key}.${childKey}`;
          addOption(
            optionId,
            childKey,
            formatCounterLabel(childKey),
            childValue,
            key,
          );
        }
      });
      processedKeys.add(key);
    }
  };

  const preferredOrder = ["active", "inactive", "paid", "free"];
  preferredOrder.forEach((key) => {
    if (key in counters) {
      handleEntry(key, counters[key] as number | Record<string, unknown>);
    }
  });

  Object.entries(counters).forEach(([key, value]) => {
    if (processedKeys.has(key)) {
      return;
    }
    handleEntry(key, value as number | Record<string, unknown>);
  });

  return options;
};

const CounterFilters: React.FC<CounterFiltersProps> = ({
  options,
  activeFilterId,
  onFilterChange,
  totalCount,
  className,
  status,
}) => {
  const languageContext = useLanguage();
  const translate = (key: string) =>
    typeof languageContext?.t === "function" ? languageContext.t(key) : key;

  const translateFilterLabel = (label: string): string => {
    const labelKey = label.toLowerCase().replace(/\s+/g, "");

    const translationMap: Record<string, string> = {
      active: "active",
      inactive: "inactive",
      paid: "paid",
      free: "free",
      callback: "callBack",
      notresponding: "notResponding",
      not_responding: "notResponding",
      onlyonline: "onlyOnline",
      onlyoffline: "onlyOffline",
      onlineoffline: "onlineOffline",
      createdbysuperadmin: "createdBySuperAdmin",
      createdbyothermediator: "createdBySuperAdmin",
    };

    const translationKey = translationMap[labelKey];

    if (translationKey) {
      const translated = translate(translationKey);
      return translated === translationKey ? label : translated;
    }

    return label;
  };

  if (!options.length) {
    return null;
  }

  const containerClass = className
    ? `overflow-x-auto ${className}`
    : "overflow-x-auto";

  const baseButtonClass =
    "flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm";
  const activeButtonClass = "bg-pink-600 text-white border-pink-600 shadow-sm";
  const inactiveButtonClass =
    "bg-white text-gray-700 border-gray-200 hover:border-pink-400 hover:text-pink-600";
  const disabledButtonClass = "opacity-40 cursor-not-allowed";

  const badgeClass =
    "ml-1 inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-semibold sm:text-xs";

  const handleSelect = (id: string | null, disabled?: boolean) => {
    if (disabled) return;
    if (id === null) {
      if (activeFilterId !== null) {
        onFilterChange(null);
      }
      return;
    }
    onFilterChange(activeFilterId === id ? null : id);
  };

  const isAllActive = activeFilterId === null;
  const allButtonClass = `${baseButtonClass} ${
    isAllActive
      ? activeButtonClass
      : "bg-white text-pink-700 border-pink-200 hover:border-pink-400 hover:text-pink-600"
  }`;
  const allBadgeClass = `${badgeClass} ${
    isAllActive ? "bg-white/20 text-white" : "bg-pink-100 text-pink-700"
  }`;

  return (
    <div className={containerClass}>
      <div className="flex min-w-max gap-2 pr-3">
        <button
          type="button"
          onClick={() => handleSelect(null)}
          className={allButtonClass}
        >
          <span>{translate("matches.filters.all")}</span>
          <span className={allBadgeClass}>
            {Number.isFinite(totalCount) ? totalCount : 0}
          </span>
        </button>
        {options.map((option) => {
          const isActive = option.id === activeFilterId;
          const isDisabled = option.count <= 0;
          if (
            status === "other" &&
            (option.id === "inactive" || option.id === "active")
          )
            return null;
          if (option.id == "all") return null;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => handleSelect(option.id, isDisabled)}
              className={`${baseButtonClass} ${
                isActive
                  ? activeButtonClass
                  : `${inactiveButtonClass} ${
                      isDisabled ? disabledButtonClass : ""
                    }`
              }`}
              aria-pressed={isActive}
              disabled={isDisabled}
            >
              {/* Use translated label instead of original */}
              <span>{translateFilterLabel(option.label)}</span>
              <span
                className={`${badgeClass} ${
                  isActive
                    ? "bg-white/20 text-white"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {option.count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

type MatchListPageConfig = {
  getEndpoint: (params: {
    gender?: string;
    status?: string;
    filter?: string;
  }) => string;
  getHeaderTitle: (params: {
    gender?: string;
    status?: string;
    filter?: string;
  }) => string;
  emptyMessage?: string;
  transform?: (payload: unknown) => MatchListEntry[];
  hideCounterFilters?:
    | boolean
    | ((params: { gender?: string; status?: string }) => boolean);
  hideMobileGnavbar?: boolean;
  getGenderSwitchPath?: (
    params: { gender?: string; status?: string },
    genderKey: "all" | "male" | "female",
  ) => string | undefined;
  buildExtraActions?: (args: {
    match: MatchListEntry;
    updateFollowUpStatus: (
      match: MatchListEntry,
      nextStatus: "callback" | "not_responding",
    ) => Promise<void>;
    updatePaymentStatus: (
      match: MatchListEntry,
      nextStatus: "free" | "paid" | "redzone",
    ) => Promise<void>;
    endpoint: string;
  }) => MatchCardAction[];
};

const VIEW_MODE_STORAGE_KEY = "matches_view_mode";
const FILTER_STORAGE_BASE_KEY = "matches_active_filter";

const buildFilterStorageKey = (gender?: string, status?: string): string => {
  const genderKey = gender?.toLowerCase() || "all";
  const statusKey = status?.toLowerCase() || "default";
  return `${FILTER_STORAGE_BASE_KEY}:${genderKey}:${statusKey}`;
};

const readStoredFilter = (storageKey: string): string | null => {
  if (typeof window === "undefined") return null;
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
};

const writeStoredFilter = (storageKey: string, filter: string | null): void => {
  if (typeof window === "undefined") return;
  try {
    if (filter) {
      localStorage.setItem(storageKey, filter);
    } else {
      localStorage.removeItem(storageKey);
    }
  } catch {
    /* ignore persistence errors */
  }
};

const readStoredViewMode = (): "list" | "grid" => {
  if (typeof window === "undefined") return "list";
  try {
    const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return stored === "grid" ? "grid" : "list";
  } catch {
    return "list";
  }
};

const createMatchListPage = ({
  getEndpoint,
  getHeaderTitle,
  emptyMessage = "No profiles found right now.",
  transform,
  hideCounterFilters: hideCounterFiltersConfig = false,
  hideMobileGnavbar = false,
  getGenderSwitchPath,
  buildExtraActions,
}: MatchListPageConfig) => {
  const MatchListPage: FC = () => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams<{ gender?: string; status?: "other" | "own" }>();
    const hideCounterFilters =
      typeof hideCounterFiltersConfig === "function"
        ? hideCounterFiltersConfig(params)
        : hideCounterFiltersConfig;
    const bureauInfo = useSelector((state: RootState) => state?.bureau.data);
    const hasPaidMembership = hasActiveMembership(bureauInfo?.paymentStatus);
    const filterStorageKey = useMemo(
      () => buildFilterStorageKey(params.gender, params.status),
      [params.gender, params.status],
    );

    const [viewMode, setViewMode] = useState<"list" | "grid">(
      readStoredViewMode,
    );
    const [showStickyControls, setShowStickyControls] = useState(false);
    // const [activeFilterId, setActiveFilterId] = useState<string | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeFilterId = searchParams.get("filter"); // null means All
    const hasRestoredFilter = useRef(false);

    const endpoint = getEndpoint({
      ...params,
      filter: activeFilterId ?? undefined,
    });

    // Translate the header title
    const rawHeaderTitle = getHeaderTitle({
      ...params,
      filter: activeFilterId ?? undefined,
    });
    const headerTitle = useMemo(() => {
      const directTranslation = t(rawHeaderTitle);
      if (directTranslation !== rawHeaderTitle) {
        return directTranslation;
      }

      // Extract parts from the raw title to translate
      const lowerTitle = rawHeaderTitle.toLowerCase();

      // Gender translation
      let translatedTitle = rawHeaderTitle;
      if (lowerTitle.includes("male")) {
        const genderText = lowerTitle.includes("female")
          ? t("Female")
          : t("Male");
        translatedTitle = translatedTitle.replace(/female|male/i, genderText);
      }

      // Status translation
      if (lowerTitle.includes("pending")) {
        translatedTitle = translatedTitle.replace(/pending/i, t("Pending"));
      }
      if (lowerTitle.includes("shortlisted")) {
        translatedTitle = translatedTitle.replace(
          /shortlisted/i,
          t("Shortlisted"),
        );
      }
      if (lowerTitle.includes("own")) {
        translatedTitle = translatedTitle.replace(
          /own/i,
          t("matches.status.own"),
        );
      }
      if (
        lowerTitle.includes("other") &&
        lowerTitle.includes("profiles") &&
        !lowerTitle.includes("other bureau")
      ) {
        translatedTitle = translatedTitle.replace(/\bother\b/i, t("Other Bureau"));
      }
      if (lowerTitle.includes("all")) {
        translatedTitle = translatedTitle.replace(
          /all/i,
          t("matches.filters.all"),
        );
      }

      translatedTitle = translatedTitle.replace(/profiles/i, t("profiles"));

      return translatedTitle;
    }, [rawHeaderTitle, t]);

    const {
      matches,
      setMatches,
      loading,
      loadingMore,
      error,
      refetch,
      loadMore,
      hasMore,
      total,
      counters,
    } = useMatchEndpoint(endpoint, {
      transform,
      pageSize: 10,
    });

    useEffect(() => {
      if (loading || matches.length === 0) return;

      try {
        const currentPath = window.location.pathname + window.location.search;
        const scopedKey = `matches:scroll:${currentPath}`;
        let rawY = sessionStorage.getItem(scopedKey);

        if (!rawY) {
          const lastPath = sessionStorage.getItem("matches:lastPath");
          if (lastPath !== currentPath) return;
          rawY = sessionStorage.getItem("matches:lastScrollY");
        }

        const y = rawY ? Number(rawY) : 0;
        setTimeout(() => {
          window.scrollTo(0, y);
        }, 200);

        sessionStorage.removeItem(scopedKey);
        sessionStorage.removeItem("matches:lastScrollY");
        sessionStorage.removeItem("matches:lastPath");
      } catch {}
    }, [location.key, loading, matches.length]);

    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [currentProfile, setCurrentProfile] = useState<MatchListEntry | null>(
      null,
    );
    const [reportReason, setReportReason] = useState("");
    const [reportDescription, setReportDescription] = useState("");
    const [reportLoading, setReportLoading] = useState(false);
    const [followUpUpdating, setFollowUpUpdating] = useState<string | null>(
      null,
    );

    useEffect(() => {
      if (typeof window === "undefined") return;
      try {
        localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
      } catch {
        /* ignore persistence errors */
      }
    }, [viewMode]);

    useEffect(() => {
      hasRestoredFilter.current = false;
    }, [filterStorageKey]);

    useEffect(() => {
      if (!hideCounterFilters || !activeFilterId) {
        return;
      }

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("filter");
          next.delete("page");
          return next;
        },
        { replace: true },
      );
    }, [activeFilterId, hideCounterFilters, setSearchParams]);

    useEffect(() => {
      if (hasRestoredFilter.current) return;
      if (activeFilterId) {
        hasRestoredFilter.current = true;
        return;
      }

      const storedFilter = readStoredFilter(filterStorageKey);
      if (!storedFilter) {
        hasRestoredFilter.current = true;
        return;
      }

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("filter", storedFilter);
          next.delete("page");
          return next;
        },
        { replace: true },
      );
      hasRestoredFilter.current = true;
    }, [activeFilterId, filterStorageKey, setSearchParams]);

    useEffect(() => {
      writeStoredFilter(filterStorageKey, activeFilterId);
    }, [filterStorageKey, activeFilterId]);

    const reportReasons = useMemo(
      () => [
        "Inappropriate Content",
        "Fake Profile",
        "Spam or Scam",
        "Harassment",
        "Impersonation",
        "Misleading Information",
        "Other",
      ],
      [],
    );

    const selectedProfileMartialId = useMemo(() => {
      if (!currentProfile) return null;
      const id = currentProfile.maritalId;
      return id ?? null;
    }, [currentProfile]);

    const openReportModal = (match: MatchListEntry) => {
      setCurrentProfile(match);
      setReportReason("");
      setReportDescription("");
      setIsReportModalOpen(true);
    };

    const openShareModal = (match: MatchListEntry) => {
      setCurrentProfile(match);
      setIsShareModalOpen(true);
    };

    const handleTakeProfile = (match: MatchListEntry) => {
      const bureauMobileNumber = String(bureauInfo?.mobileNumber || "").trim();
      const targetNumber = bureauMobileNumber.replace(/\D/g, "");

      if (!targetNumber) {
        toast.error("Your bureau mobile number is not available.");
        return;
      }

      const message = [
        "I need this profile",
        `Profile ID: ${match.maritalId || "N/A"}`,
        `Name: ${match.fullName || "N/A"}`,
        `Bureau Name: ${bureauInfo?.bureauName || "Self Registered"}`,
        `Mobile Number: ${bureauMobileNumber || "N/A"}`,
      ].join("\n");

      const whatsappUrl = `https://wa.me/${targetNumber}?text=${encodeURIComponent(message)}`;

      window.open(whatsappUrl, "_blank", "noopener,noreferrer");
    };

    // Same WhatsApp action as Full Profile Manage (own bureau profiles only).
    const handleWhatsAppProfile = (match: MatchListEntry) => {
      if (
        match.bureauId &&
        bureauInfo?.bureauId &&
        String(match.bureauId) !== String(bureauInfo.bureauId)
      ) {
        toast.error("WhatsApp is available only for your bureau's profiles.");
        return;
      }
      sendLoginCredentialsOnWhatsApp(
        match,
        bureauInfo?.websiteUrl,
        bureauInfo?.websiteLinks,
      );
    };

    const handleSwitchClick = (match: MatchListEntry) => {
      if (!hasPaidMembership) {
        navigate("/admin/membership/plans", { state: { fromShare: true } });

        return;
      }
      handleSwitchProfile(match);
    };

    const updateFollowUpStatus = async (
      match: MatchListEntry,
      nextStatus: "callback" | "not_responding",
    ) => {
      const recordId = match.recordId ?? match.id;
      if (!recordId) {
        toast.error("Unable to determine profile ID.");
        return;
      }

      setFollowUpUpdating(`${recordId}:${nextStatus}`);
      try {
        const response = await putRequest(`/bureau/profile/${recordId}`, {
          followUpStatus: nextStatus,
        });

        if (!response?.success) {
          toast.error(
            response?.message ?? "Unable to update follow-up status.",
          );
          return;
        }

        const updatedMatch = {
          ...match,
          followUpStatus: nextStatus,
        };

        const shouldRemoveFromCurrentList = !endpoint.includes(
          `filter=${nextStatus}`,
        );

        if (shouldRemoveFromCurrentList) {
          setMatches((prevMatches) =>
            prevMatches.filter(
              (entry) => (entry.recordId ?? entry.id) !== recordId,
            ),
          );
        } else {
          setMatches((prevMatches) =>
            prevMatches.map((entry) =>
              (entry.recordId ?? entry.id) === recordId ? updatedMatch : entry,
            ),
          );
        }

        toast.success("Follow-up status updated.");
      } finally {
        setFollowUpUpdating(null);
      }
    };

    const updatePaymentStatus = async (
      match: MatchListEntry,
      nextStatus: "free" | "paid" | "redzone",
    ) => {
      const recordId = match.recordId ?? match.id;
      if (!recordId) {
        toast.error("Unable to determine profile ID.");
        return;
      }

      setFollowUpUpdating(`${recordId}:${nextStatus}`);
      try {
        const response = await putRequest(`/bureau/profile/${recordId}`, {
          paymentStatus: nextStatus,
          followUpStatus: "none",
        });

        if (!response?.success) {
          toast.error(response?.message ?? "Unable to update profile status.");
          return;
        }

        const updatedMatch = {
          ...match,
          paymentStatus: nextStatus,
          followUpStatus: "none",
        };

        const shouldRemoveFromCurrentList = !endpoint.includes(
          `filter=${nextStatus}`,
        );

        if (shouldRemoveFromCurrentList) {
          setMatches((prevMatches) =>
            prevMatches.filter(
              (entry) => (entry.recordId ?? entry.id) !== recordId,
            ),
          );
        } else {
          setMatches((prevMatches) =>
            prevMatches.map((entry) =>
              (entry.recordId ?? entry.id) === recordId ? updatedMatch : entry,
            ),
          );
        }

        toast.success("Profile status updated.");
      } finally {
        setFollowUpUpdating(null);
      }
    };

    const closeReportModal = () => {
      setIsReportModalOpen(false);
      setCurrentProfile(null);
      setReportReason("");
      setReportDescription("");
    };

    const handleReportSubmit = async () => {
      if (!currentProfile?.maritalId) {
        toast.error("Unable to determine profile ID for report");
        return;
      }

      if (!reportReason.trim()) {
        toast.error("Please select a reason for reporting");
        return;
      }

      setReportLoading(true);
      try {
        const payload: Record<string, unknown> = {
          reportedProfileId: currentProfile.maritalId,
          reason: reportReason,
          description: reportDescription,
          reportedProfileName: currentProfile.fullName,
          reportedAt: new Date().toISOString(),
        };

        if (selectedProfileMartialId) {
          payload.reportedMartialId = selectedProfileMartialId;
        }

        if (bureauInfo?.bureauId) {
          payload.reporterBureauId = bureauInfo?.bureauId;
        }

        const response = await postRequest(`/bureau/profiles/report`, payload);
        if (response?.success) {
          toast.success(response?.message ?? "Profile reported successfully");
          closeReportModal();
        } else {
          toast.error(response?.message ?? "Failed to submit report");
        }
      } catch (error) {
        console.error("Error reporting profile:", error);
        toast.error("Error reporting profile. Please try again.");
      } finally {
        setReportLoading(false);
      }
    };

    // Refetch when endpoint changes (route params change)
    // useEffect(() => {
    //   refetch();
    // }, [endpoint, refetch]);

    useEffect(() => {
      const handleScroll = () => {
        setShowStickyControls(window.scrollY > 80);
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      handleScroll();

      return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const filterOptions = useMemo(() => {
      const options = buildFilterOptions(counters);
      if (
        activeFilterId &&
        !options.some((option) => option.id === activeFilterId)
      ) {
        options.push({
          id: activeFilterId,
          label: formatCounterLabel(activeFilterId),
          count: matches.length,
          predicate: () => true,
        });
      }
      return options;
    }, [counters, activeFilterId, matches.length]);

    useEffect(() => {
      if (
        activeFilterId &&
        !filterOptions.some((option) => option.id === activeFilterId)
      ) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("filter");
            next.delete("page"); // optional
            return next;
          },
          { replace: true },
        ); // replace => avoid adding extra history entries
      }
    }, [activeFilterId, filterOptions, setSearchParams]);

    const filteredMatches = matches;

    const visibleMatches = filteredMatches.length;
    const activeFilterLabel = activeFilterId
      ? (filterOptions.find((option) => option.id === activeFilterId)?.label ??
        null)
      : null;
    const shouldHideActiveFilterSummary = useMemo(() => {
      const normalizedFilter = normalizeKeyValue(activeFilterId);
      return normalizedFilter === "createdbyothermediator";
    }, [activeFilterId]);

    const matchCountLabel = useMemo(() => {
      const hasTotal = typeof total === "number" && total > 0;
      const baseTemplate = hasTotal
        ? t("matches.count.withTotal")
        : t("matches.count.noTotal");
      const totalValue = hasTotal ? (total ?? visibleMatches) : visibleMatches;
      const baseLabel = baseTemplate
        .replace("{visible}", String(visibleMatches))
        .replace("{total}", String(totalValue));
      return activeFilterLabel && !shouldHideActiveFilterSummary
        ? `${baseLabel} • ${activeFilterLabel}`
        : baseLabel;
    }, [visibleMatches, total, activeFilterLabel, shouldHideActiveFilterSummary, t]);

    const resolvedEmptyMessage =
      emptyMessage === "No profiles found right now."
        ? t("matches.messages.empty")
        : emptyMessage;

    const totalForFilters = useMemo(() => {
      if (typeof counters?.all === "number") {
        return counters.all;
      }
      if (typeof total === "number" && total > 0) {
        return total;
      }
      return matches.length;
    }, [counters, total, matches.length]);
    const renderFilterControls = (instanceKey: string) =>
      !hideCounterFilters &&
      filterOptions.length > 0 &&
      params.status !== "other" ? (
        <CounterFilters
          key={`${instanceKey}-${activeFilterId ?? "all"}`}
          options={filterOptions}
          activeFilterId={activeFilterId}
          onFilterChange={(id) => {
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                if (!id) next.delete("filter");
                else next.set("filter", id);

                // pagination ఉంటే reset చేయండి
                next.delete("page");
                return next;
              },
              { replace: false },
            ); // false => history push, back works
          }}
          totalCount={totalForFilters}
          className="scrollbar-hide"
          status={params.status}
        />
      ) : null;

    const activeGenderKey = (params.gender || "all").toLowerCase();
    const genderSwitchOptions: { key: "all" | "male" | "female"; label: string }[] = [
      { key: "all", label: t("matches.filters.all") },
      { key: "male", label: t("Male") },
      { key: "female", label: t("Female") },
    ];
    const genderSwitchTargets = useMemo(() => {
      if (!getGenderSwitchPath) return null;
      const entries = genderSwitchOptions
        .map((option) => [option.key, getGenderSwitchPath(params, option.key)] as const)
        .filter((entry): entry is [typeof entry[0], string] => Boolean(entry[1]));
      return entries.length > 0 ? entries : null;
    }, [getGenderSwitchPath, params.gender, params.status]);

    const genderSwitchControls = genderSwitchTargets ? (
      <div className="flex gap-2 rounded-lg bg-gray-100 p-1 w-fit">
        {genderSwitchTargets.map(([key, target]) => {
          const isActive = activeGenderKey === key;
          const label = genderSwitchOptions.find((option) => option.key === key)?.label ?? key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => navigate(target)}
              className={`px-3 py-1.5 rounded text-xs font-semibold transition-all ${
                isActive
                  ? "bg-white text-pink-600 shadow-sm"
                  : "text-gray-600 hover:text-pink-600"
              }`}
              aria-pressed={isActive}
            >
              {label}
            </button>
          );
        })}
      </div>
    ) : null;

    const stickyFilterControls = genderSwitchControls ? (
      <div className="flex flex-wrap items-center gap-2">
        {genderSwitchControls}
        {renderFilterControls("sticky")}
      </div>
    ) : (
      renderFilterControls("sticky")
    );
    const headerFilterControls = genderSwitchControls ? (
      <div className="flex flex-wrap items-center gap-2">
        {genderSwitchControls}
        {renderFilterControls("main")}
      </div>
    ) : (
      renderFilterControls("main")
    );
    const getExtraActions = buildExtraActions
      ? (match: MatchListEntry) =>
          buildExtraActions({
            match,
            updateFollowUpStatus,
            updatePaymentStatus,
            endpoint,
          }).map((action) => ({
            ...action,
            disabled:
              action.disabled ||
              followUpUpdating?.startsWith(`${match.recordId ?? match.id}:`) ||
              false,
          }))
      : undefined;

    const canShowStickyBar =
      !loading && !error && matches.length > 0 && !loadingMore;
    const revealStickyControls = canShowStickyBar && showStickyControls;
    const mainContentClassName = `min-h-screen bg-gray-50 ${
      hideMobileGnavbar ? "pt-4 mt-0 md:pt-15 md:pb-15 md:mt-16" : "pt-15 pb-15 mt-16"
    }`;

    return (
      <div>
        <div className="fixed top-0 left-0 right-0 z-50">
          {/* Navbar */}
          <div
            className={`transition-transform duration-300 ${
              revealStickyControls ? "-translate-y-full" : ""
            } ${hideMobileGnavbar ? "hidden md:block" : ""}`}
          >
            <Gnavbar />
          </div>

          {/* Sticky Bar - positioned absolutely to overlay at top */}
          {canShowStickyBar && (
            <div
              className={`safe-area-top absolute top-0 left-0 right-0 bg-white shadow transition-all duration-300 ${
                revealStickyControls
                  ? "translate-y-0 opacity-100 visible"
                  : "-translate-y-full opacity-0 invisible"
              }`}
            >
              <div className="max-w-7xl mx-auto px-4 py-2">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-[180px]">
                    <h2 className="text-sm font-semibold text-gray-900 sm:text-base">
                      {headerTitle}
                    </h2>
                    <p className="text-xs text-gray-500 sm:text-sm">
                      {matchCountLabel}
                    </p>
                  </div>
                  <div className="ml-auto flex gap-2 rounded-lg bg-gray-100 p-1">
                    <button
                      type="button"
                      onClick={() => setViewMode("list")}
                      className={`p-2 rounded transition-all ${
                        viewMode === "list"
                          ? "bg-white text-pink-600 shadow-sm"
                          : "text-gray-600 hover:text-pink-600"
                      }`}
                      aria-pressed={viewMode === "list"}
                      aria-label="List view"
                    >
                      <List size={20} />
                    </button>
                    <button
                      type="button"
                      onClick={() => setViewMode("grid")}
                      className={`p-2 rounded transition-all ${
                        viewMode === "grid"
                          ? "bg-white text-pink-600 shadow-sm"
                          : "text-gray-600 hover:text-pink-600"
                      }`}
                      aria-pressed={viewMode === "grid"}
                      aria-label="Grid view"
                    >
                      <Grid size={20} />
                    </button>
                  </div>
                </div>
                {stickyFilterControls && (
                  <div className="mt-2">{stickyFilterControls}</div>
                )}
              </div>
            </div>
          )}
        </div>
        <main className={mainContentClassName}>
          {loading ? (
            <div className="flex justify-center items-center py-20 text-purple-500">
              <FaSpinner className="animate-spin text-3xl" />
            </div>
          ) : error ? (
            <div className="max-w-md mx-auto text-center space-y-4 px-4 py-12">
              <FaExclamationTriangle className="mx-auto text-3xl text-red-500" />
              <p className="text-gray-700">{error}</p>
              <button
                type="button"
                onClick={refetch}
                className="inline-flex items-center px-5 py-2 rounded-lg bg-purple-600 text-white font-semibold shadow hover:bg-purple-700 transition"
              >
                {t("matches.buttons.retry")}
              </button>
            </div>
          ) : matches.length === 0 ? (
            <div className="max-w-md mx-auto text-center space-y-4 px-4 py-12 text-gray-600">
              <p>{resolvedEmptyMessage}</p>
              <button
                type="button"
                onClick={refetch}
                className="inline-flex items-center px-5 py-2 rounded-lg bg-gray-200 text-gray-700 font-semibold hover:bg-gray-300 transition"
              >
                {t("matches.buttons.refresh")}
              </button>
            </div>
          ) : (
            <MatchesContainer
              title={headerTitle}
              matches={matches}
              setProfiles={setMatches}
              totalCount={total}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onLoadMore={loadMore}
              onViewProfile={(match: MatchListEntry) =>
                handleViewProfile(match, navigate)
              }
              onAddToShortlist={handleShortList}
              onSwitchProfile={handleSwitchClick}
              onShareProfile={openShareModal}
              onManageProfile={(match: MatchListEntry) =>
                handleManageProfile(match, navigate, {
                  returnTo: `${location.pathname}${location.search}`,
                })
              }
              onReportProfile={openReportModal}
              getExtraActions={getExtraActions}
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              matchCountLabel={matchCountLabel}
              headerExtras={headerFilterControls}
              onTakeProfile={handleTakeProfile}
              onWhatsAppProfile={handleWhatsAppProfile}
            />
          )}
        </main>
        {isReportModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-red-600">
                  Report Profile
                </h2>
                <button
                  onClick={closeReportModal}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close report modal"
                >
                  <FaTimes size={20} />
                </button>
              </div>

              {currentProfile && (
                <div className="mb-4 p-3 bg-gray-50 rounded">
                  <p className="text-sm text-gray-600">Reporting Profile:</p>
                  <p className="font-semibold">
                    {currentProfile.fullName || "Unknown User"}
                  </p>
                  <p className="text-sm text-gray-600">
                    Marital ID: {selectedProfileMartialId ?? "N/A"}
                  </p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Reporting *
                </label>
                <select
                  value={reportReason}
                  onChange={(event) => setReportReason(event.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  required
                >
                  <option value="">Select a reason...</option>
                  {reportReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Details (Optional)
                </label>
                <textarea
                  value={reportDescription}
                  onChange={(event) => setReportDescription(event.target.value)}
                  placeholder="Please provide more details about your report..."
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  rows={4}
                />
              </div>

              <div className="flex space-x-4">
                <button
                  onClick={closeReportModal}
                  className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  disabled={reportLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleReportSubmit}
                  disabled={reportLoading || !reportReason.trim()}
                  className="flex-1 py-2 px-4 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {reportLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Reporting...
                    </>
                  ) : (
                    "Submit Report"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        {isShareModalOpen && currentProfile && (
          <ShareModal
            profile={currentProfile}
            setIsShareModalOpen={setIsShareModalOpen}
          />
        )}
        <BottomNavbar />
      </div>
    );
  };

  return MatchListPage;
};

export default createMatchListPage;
